import React, { useCallback, useMemo } from "react";
import ReactCountryFlag from "react-country-flag";
import styled from "styled-components";
import useTranslation from "../../hooks/useTranslation";

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const Label = styled.label`
  font-size: calc(14 / 1440 * 100vw);
  color: #333;
  margin-bottom: calc(6 / 1440 * 100vw);
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: calc((14 / 375) * 100vw);
    margin-bottom: calc((6 / 375) * 100vw);
  }
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const CountryPrefix = styled.div`
  position: absolute;
  left: calc((14 / 1440) * 100vw);
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: calc(14 / 1440 * 100vw);
  color: #333;
  z-index: 1;

  @media (max-width: 768px) {
    left: calc((14 / 375) * 100vw);
    font-size: calc((14 / 375) * 100vw);
  }
`;

const StyledInput = styled.input`
  width: 100%;
  padding: calc((13 / 1440) * 100vw) calc((14 / 1440) * 100vw);
  padding-left: calc((90 / 1440) * 100vw); /* Space for flag and dial code */
  border: 1px solid ${(props) => (props.$invalid ? "#ef4444" : "#e2e8f0")};
  border-radius: calc((8 / 1440) * 100vw);
  font-size: calc(14 / 1440 * 100vw);

  &:disabled {
    background: #f1f5f9;
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 1px #3b82f6;
  }

  @media (max-width: 768px) {
    font-size: calc((14 / 375) * 100vw);
    padding: calc((13 / 375) * 100vw) calc((14 / 375) * 100vw);
    padding-left: calc((90 / 375) * 100vw);
    border-radius: calc((8 / 375) * 100vw);
  }
`;

const ErrorMessage = styled.span`
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;

  @media (max-width: 768px) {
    font-size: calc((12 / 375) * 100vw);
  }
`;

const PhoneInputField = React.memo(
  ({ onChange, value, errMessage, disabled }) => {
    const { t } = useTranslation();
    
    // Saudi Arabia constants
    const SAUDI_DIAL_CODE = "+966";
    const SAUDI_COUNTRY_CODE = "SA";

    // Extract the phone number without the dial code
    const phoneNumber = useMemo(() => {
      if (!value) return "";
      if (value.startsWith(SAUDI_DIAL_CODE)) {
        return value.slice(SAUDI_DIAL_CODE.length);
      }
      return value;
    }, [value]);

    const handleNumberChange = useCallback(
      (e) => {
        const newNumber = e.target.value.replace(/^0+/, ""); // Remove leading zeros
        // Only allow numbers
        const numbersOnly = newNumber.replace(/\D/g, "");
        onChange(`${SAUDI_DIAL_CODE}${numbersOnly}`);
      },
      [onChange]
    );

    return (
      <Container>
        <FormGroup>
          <Label>{t.phoneNumber}</Label>
          <InputWrapper>
            <CountryPrefix>
              <ReactCountryFlag countryCode={SAUDI_COUNTRY_CODE} svg />
              <span>{SAUDI_DIAL_CODE}</span>
            </CountryPrefix>
            <StyledInput
              disabled={disabled}
              id="mobile"
              type="tel"
              placeholder={t.enterPhoneNumber}
              $invalid={!!errMessage}
              value={phoneNumber}
              onChange={handleNumberChange}
              maxLength="9" // Saudi mobile numbers are typically 9 digits after the country code
            />
          </InputWrapper>
        </FormGroup>
        {errMessage && <ErrorMessage>{errMessage}</ErrorMessage>}
      </Container>
    );
  }
);

PhoneInputField.displayName = "PhoneInputField";

export default PhoneInputField;